import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class TktAvailabilityLayout implements Plugin {
  private layout: HTMLElement | null;
  private mediaQuery: MediaQueryList;
  private handleResize: (e: MediaQueryListEvent) => void;
  private sortButton: HTMLElement | null = null;
  private sortButtonModalValue: string | null = null;
  private dropdownChangeHandler: (e: CustomEvent) => void;
  private desktopDropdownChangeHandler: (e: CustomEvent) => void;
  private modalOpenHandler: () => void;
  private selectedSortValue: string | null = null;
  private selectedSortLabel: string | null = null;

  constructor(private readonly element: HTMLElement) {
    this.layout = element.querySelector(".tkt-availability__layout");
    this.mediaQuery = window.matchMedia("(max-width: 767px)");
    this.handleResize = (e) => this.checkResolution(e.matches);
    this.dropdownChangeHandler = (e) => this.handleDropdownChange(e);
    this.desktopDropdownChangeHandler = (e) =>
      this.handleDesktopDropdownChange(e);
    this.modalOpenHandler = () => this.handleModalOpen();
  }

  init(): void {
    if (!this.layout) return;
    this.element.addEventListener("segmentChanged", (e: Event) => {
      const customEvent = e as CustomEvent;
      const { tabId } = customEvent.detail;
      this.handleViewChange(tabId);
    });

    this.layout.addEventListener(
      "coreDropdown:change",
      this.dropdownChangeHandler as EventListener,
    );

    const desktopDropdownWrapper = this.layout?.querySelector(
      ".core-buttonSelect-wrapper",
    );
    if (desktopDropdownWrapper) {
      desktopDropdownWrapper.addEventListener(
        "buttonSelectDropdown:change",
        this.desktopDropdownChangeHandler as EventListener,
      );
    }

    this.sortButton =
      this.layout?.querySelector("[data-modal='modal-sort']") || null;
    this.sortButtonModalValue =
      this.sortButton?.getAttribute("data-modal") || null;
    this.initializeSelectedValue();

    document.addEventListener("modal:opened", this.modalOpenHandler);

    const modalContainer = document.querySelector("[id='modal-sort']");
    if (modalContainer) {
      const observer = new MutationObserver(() => {
        if (modalContainer.getAttribute("aria-hidden") === "false") {
          this.syncModalSelection();
        }
      });
      observer.observe(modalContainer, {
        attributes: true,
        attributeFilter: ["aria-hidden"],
      });
    }

    this.mediaQuery.addEventListener("change", this.handleResize);

    this.checkResolution(this.mediaQuery.matches);
  }

  private checkResolution(isMobile: boolean): void {
    document.dispatchEvent(
      new CustomEvent("buttonSelectDropdown:disable", {
        detail: {
          disable: isMobile,
          scope: ".tkt-availability__layout",
        },
      }),
    );

    if (this.sortButton) {
      if (isMobile) {
        if (this.sortButtonModalValue) {
          this.sortButton.setAttribute("data-modal", this.sortButtonModalValue);
        }
      } else {
        this.sortButton.removeAttribute("data-modal");
        if (this.selectedSortValue) {
          const desktopDropdown = this.layout?.querySelector(
            ".core-buttonSelect-wrapper",
          );
          console.log(desktopDropdown);
          if (desktopDropdown) {
            desktopDropdown.dispatchEvent(
              new CustomEvent("buttonSelectDropdown:select", {
                bubbles: true,
                detail: {
                  value: this.selectedSortValue,
                  scope: ".tkt-availability__layout",
                },
              }),
            );
          }
        }
      }
    }
  }

  private handleViewChange(viewId: string): void {
    if (!this.layout) return;

    const listResult = this.element.querySelector(
      ".tkt-availability__list-result",
    );
    const cards = this.element.querySelectorAll(".tkt-cardB2B");

    if (!listResult) return;

    const DURATION_MS = 150;

    // 1. Fade out
    listResult.classList.add("tkt-availability__list-result--fade-out");

    // 2. Tras el fade out: cambiar layout y fade in
    setTimeout(() => {
      if (viewId.includes("grid")) {
        this.layout.classList.add("tkt-availability__layout--grid");
        this.updateCards(cards, "vertical");
      } else {
        this.layout.classList.remove("tkt-availability__layout--grid");
        this.updateCards(cards, "horizontal");
      }
      listResult.classList.remove("tkt-availability__list-result--fade-out");
    }, DURATION_MS);
  }

  private handleDropdownChange(event: CustomEvent): void {
    const { value, label, element } = event.detail;

    const modalContent = element?.closest("[id='modal-sort']");
    if (!modalContent) return;

    this.selectedSortValue = value;
    this.selectedSortLabel = label;

    this.updateButtonLabel(label);

    if (!this.mediaQuery.matches) {
      const desktopDropdown = this.layout?.querySelector(
        ".core-buttonSelect-wrapper",
      );
      if (desktopDropdown) {
        desktopDropdown.dispatchEvent(
          new CustomEvent("buttonSelectDropdown:select", {
            bubbles: true,
            detail: {
              value: value,
              scope: ".tkt-availability__layout",
            },
          }),
        );
      }
    }
  }

  private handleDesktopDropdownChange(event: CustomEvent): void {
    const { value, label } = event.detail;

    this.selectedSortValue = value || null;
    this.selectedSortLabel = label || null;

    if (!value) {
      const modal = document.querySelector("[id='modal-sort']");
      if (modal) {
        const checkedRadio = modal.querySelector<HTMLInputElement>(
          "input[type='radio']:checked",
        );
        if (checkedRadio) {
          checkedRadio.checked = false;
        }
      }
    } else {
      this.syncModalSelection();
    }
  }

  private handleModalOpen(): void {
    this.syncModalSelection();
  }

  private initializeSelectedValue(): void {
    const desktopDropdown = this.layout?.querySelector(
      ".core-buttonSelect-dropdown",
    );
    if (!desktopDropdown) return;

    const selectedRadio = desktopDropdown.querySelector<HTMLInputElement>(
      "input[type='radio']:checked",
    );
    if (selectedRadio) {
      this.selectedSortValue = selectedRadio.value;
      this.selectedSortLabel =
        selectedRadio.getAttribute("aria-label") || selectedRadio.value;
    }

    this.syncModalSelection();
  }

  private updateButtonLabel(label: string): void {
    if (!this.sortButton) return;
    const labelElement = this.sortButton.querySelector(
      ".core-buttonSelect__label",
    );
    if (labelElement) {
      labelElement.textContent = label;
    }
  }

  private syncModalSelection(): void {
    if (!this.selectedSortValue || !this.layout) return;

    const modal = document.querySelector("[id='modal-sort']");
    if (!modal) return;

    const modalRadio = modal.querySelector<HTMLInputElement>(
      `input[type='radio'][value='${this.selectedSortValue}']`,
    );
    if (modalRadio && !modalRadio.checked) {
      modalRadio.checked = true;
    }
  }

  private updateCards(
    cards: NodeListOf<Element>,
    display: "vertical" | "horizontal",
  ): void {
    cards.forEach((card) => {
      const el = card as HTMLElement;
      el.classList.remove(
        "tkt-cardB2B--display-horizontal",
        "tkt-cardB2B--display-vertical",
      );
      el.classList.add(`tkt-cardB2B--display-${display}`);
      el.setAttribute("data-name", `display=${display}`);
    });
  }

  destroy(): void {
    this.mediaQuery.removeEventListener("change", this.handleResize);
    this.layout?.removeEventListener(
      "coreDropdown:change",
      this.dropdownChangeHandler as EventListener,
    );

    const desktopDropdownWrapper = this.layout?.querySelector(
      ".core-buttonSelect-wrapper",
    );
    if (desktopDropdownWrapper) {
      desktopDropdownWrapper.removeEventListener(
        "buttonSelectDropdown:change",
        this.desktopDropdownChangeHandler as EventListener,
      );
    }

    document.removeEventListener("modal:opened", this.modalOpenHandler);
  }
}

PluginRegistry.register("tktAvailabilityLayout", TktAvailabilityLayout);
